import { useRef, useEffect } from "react";
import { ArrowUpRight, FileText, ScanLine, Mic, Camera, ClipboardList, Info, LayoutDashboard } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Reveal } from "@/components/Reveal";
import { DeviceFrameDesktop, DeviceFramePhone } from "@/components/DeviceFrames";
import { useLang } from "@/i18n/translations";
import { UH_BEAT_ASSETS, PROJECTS } from "@/lib/siteConfig";
import { PinnedScreenSequence } from "@/components/PinnedScreenSequence";

// Phase 12 — cinematic polish: subtle drifting zoom per flow step (no highlight)
const FLOW_FOCUS = [
  { x: 50, y: 42, scale: 1.16 },
  { x: 45, y: 45, scale: 1.2 },
  { x: 50, y: 40, scale: 1.2 },
  { x: 48, y: 46, scale: 1.18 },
  { x: 52, y: 45, scale: 1.18 },
  { x: 46, y: 42, scale: 1.2 },
  { x: 50, y: 44, scale: 1.16 },
];

const UH = PROJECTS.find((p) => p.id === "unfallheld");
const CYAN = "#22d3ee"; // Trust-Cyan — Service/Assistance Feeling, CloudService-Cyan bleibt dominant

// Lazy autoplay video: loads + plays only when visible, pauses offscreen.
const LazyVideo = ({ src, webm, poster, width, height, testId }) => {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          if (v.preload === "none") v.preload = "auto";
          v.play().catch(() => {});
        } else {
          v.pause();
        }
      },
      { threshold: 0.25 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);
  return (
    <video
      ref={ref}
      data-testid={testId}
      poster={poster}
      width={width}
      height={height}
      muted
      loop
      playsInline
      preload="none"
      className="block w-full h-auto"
    >
      <source src={src} type="video/mp4" />
      {webm && <source src={webm} type="video/webm" />}
    </video>
  );
};

const Kicker = ({ icon: Icon, children }) => (
  <p className="flex items-center gap-2 text-[11px] font-mono-cs uppercase tracking-[0.22em] text-[#00d4ff]">
    {Icon ? (
      <Icon className="w-3.5 h-3.5" style={{ color: CYAN }} />
    ) : (
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: CYAN }} aria-hidden="true" />
    )}
    {children}
  </p>
);

const Shot = ({ asset, eager = false }) => (
  <img
    src={asset.src}
    alt={asset.alt}
    width={asset.w}
    height={asset.h}
    loading={eager ? "eager" : "lazy"}
    className="block w-full h-auto"
  />
);

const Chip = ({ children }) => (
  <span
    className="px-2.5 py-1 rounded-full text-[10px] font-mono-cs uppercase tracking-[0.1em] border"
    style={{ borderColor: "rgba(34,211,238,0.35)", color: CYAN, backgroundColor: "rgba(34,211,238,0.07)" }}
  >
    {children}
  </span>
);

export const UnfallHeldCaseStudy = () => {
  const { t } = useLang();

  return (
    <section
      id="case-unfallheld"
      data-testid="case-study-unfallheld"
      className="relative py-20 sm:py-28 bg-[#070a12] border-y border-white/5 overflow-x-clip"
    >
      <div
        aria-hidden="true"
        className="absolute top-[2%] right-[-12%] w-[560px] h-[560px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(50% 50% at 50% 50%, rgba(34,211,238,0.07), transparent 70%)" }}
      />

      {/* ---- Header ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
        <Reveal>
          <Kicker>{t.uh.kicker}</Kicker>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.025em] leading-[1.08] text-foreground max-w-3xl">
            {t.uh.headline}
          </h1>
          <p className="mt-5 text-base lg:text-lg text-muted-foreground leading-relaxed max-w-2xl">{t.uh.intro}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {UH.allBadges.map((b) => (
              <Badge key={b} variant="outline" className="bg-white/5 border-white/10 text-foreground/80 font-mono-cs text-[10px] font-normal rounded-full px-2.5">
                {b}
              </Badge>
            ))}
          </div>
          <a
            data-testid="uh-visit-link"
            href={UH.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-[#00d4ff] hover:text-[#33ddff] transition-colors duration-200"
          >
            {t.uh.visit} <ArrowUpRight className="w-4 h-4" />
          </a>
        </Reveal>
      </div>

      {/* ---- Beat 1 — Problem ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-16" data-testid="uh-beat-1">
        <Reveal>
          <Kicker>{t.uh.b1Kicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.uh.b1Title}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-xl">{t.uh.b1Text}</p>
        </Reveal>
        <Reveal delay={0.12} className="mt-9">
          <DeviceFrameDesktop url="unfallheld.com" testId="uh-hero-frame">
            <Shot asset={UH_BEAT_ASSETS.heroDesktop} eager />
          </DeviceFrameDesktop>
        </Reveal>
        <Reveal delay={0.1} className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <DeviceFrameDesktop url="unfallheld.com" testId="uh-vergleich-frame">
              <Shot asset={UH_BEAT_ASSETS.vergleichDesktop} />
            </DeviceFrameDesktop>
          </div>
          <p className="lg:col-span-4 text-sm text-muted-foreground leading-relaxed font-mono-cs">{t.uh.intro}</p>
        </Reveal>
      </div>

      {/* ---- Beat 2 — Gefuehrte Unfallmeldung ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="uh-beat-2">
        <Reveal>
          <Kicker icon={ClipboardList}>{t.uh.b2Kicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.uh.b2Title}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-2xl">{t.uh.b2Text}</p>
        </Reveal>
        {/* Pinned 7-step flow sequence (Phase 12) — all 7 real intake steps */}
        <div className="mt-12">
          <PinnedScreenSequence
            steps={(t.uh.flowSeq || []).map((s, i) => ({ ...s, media: UH_BEAT_ASSETS.flowSeq[i], focus: FLOW_FOCUS[i] }))}
            accent={CYAN}
            url="unfallheld.com/unfall-melden"
            aspect="1920/1100"
            testId="uh-flow-seq"
          />
        </div>
      </div>

      {/* ---- Beat 3 — Sprechen statt tippen ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="uh-beat-3">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-4">
            <Kicker icon={Mic}>{t.uh.b3Kicker}</Kicker>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.uh.b3Title}</h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">{t.uh.b3Text}</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <DeviceFrameDesktop url="unfallheld.com/unfall-melden" testId="uh-voice-frame">
              <Shot asset={UH_BEAT_ASSETS.voiceDesktop} />
            </DeviceFrameDesktop>
          </Reveal>
        </div>
        <Reveal delay={0.12} className="mt-12 flex justify-center">
          <div className="w-[230px] sm:w-[260px]">
            <DeviceFramePhone testId="uh-transkript-phone">
              <Shot asset={UH_BEAT_ASSETS.transkriptMobile} />
            </DeviceFramePhone>
          </div>
        </Reveal>
      </div>

      {/* ---- Beat 4 — Fotos, Standort & Unfallgegner ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="uh-beat-4">
        <Reveal>
          <Kicker icon={Camera}>{t.uh.b4Kicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.uh.b4Title}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-2xl">{t.uh.b4Text}</p>
        </Reveal>
        <Reveal delay={0.1} className="mt-9">
          <DeviceFrameDesktop url="unfallheld.com/unfall-melden" testId="uh-fotos-frame">
            <Shot asset={UH_BEAT_ASSETS.fotosDesktop} />
          </DeviceFrameDesktop>
        </Reveal>
        <Reveal delay={0.1} className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <DeviceFrameDesktop url="unfallheld.com/unfall-melden" testId="uh-gegner-frame">
              <Shot asset={UH_BEAT_ASSETS.gegnerDesktop} />
            </DeviceFrameDesktop>
          </div>
          <div className="lg:col-span-4 flex justify-center">
            <div className="w-[230px] sm:w-[260px]">
              <DeviceFramePhone testId="uh-fotos-phone">
                <Shot asset={UH_BEAT_ASSETS.fotosMobile} />
              </DeviceFramePhone>
            </div>
          </div>
        </Reveal>
      </div>

      {/* ---- Beat 5 — Zulassungsschein-OCR ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="uh-beat-5">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-4">
            <Kicker icon={ScanLine}>{t.uh.b5Kicker}</Kicker>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.uh.b5Title}</h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">{t.uh.b5Text}</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <DeviceFrameDesktop url="unfallheld.com/unfall-melden" testId="uh-fahrzeug-frame">
              <Shot asset={UH_BEAT_ASSETS.fahrzeugDesktop} />
            </DeviceFrameDesktop>
          </Reveal>
        </div>
        <Reveal delay={0.12} className="mt-12 flex justify-center">
          <div className="w-[230px] sm:w-[260px]">
            <DeviceFramePhone testId="uh-ocr-phone">
              <Shot asset={UH_BEAT_ASSETS.ocrMobile} />
            </DeviceFramePhone>
          </div>
        </Reveal>
      </div>

      {/* ---- Beat 6 — KI-Ersteinschaetzung & Status ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="uh-beat-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <Kicker>{t.uh.b6Kicker}</Kicker>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.uh.b6Title}</h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">{t.uh.b6Text}</p>
            <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.03] p-6" data-testid="uh-ki-box">
              <p className="text-sm font-medium text-foreground">{t.uh.kiTitle}</p>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t.uh.kiText}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {t.uh.kiChips.map((c) => (
                  <Chip key={c}>{c}</Chip>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7">
            <DeviceFrameDesktop url="unfallheld.com/status" testId="uh-status-frame">
              <Shot asset={UH_BEAT_ASSETS.statusDesktop} />
            </DeviceFrameDesktop>
          </Reveal>
        </div>
        <Reveal delay={0.12} className="mt-12 flex justify-center gap-6 sm:gap-10">
          <div className="w-[200px] sm:w-[240px] mt-10">
            <DeviceFramePhone testId="uh-ki-phone">
              <Shot asset={UH_BEAT_ASSETS.kiMobile} />
            </DeviceFramePhone>
          </div>
          <div className="w-[200px] sm:w-[240px]">
            <DeviceFramePhone testId="uh-timeline-phone">
              <Shot asset={UH_BEAT_ASSETS.timelineMobile} />
            </DeviceFramePhone>
          </div>
        </Reveal>
      </div>

      {/* ---- Beat 7 — PDF-Schadensbericht (echtes Demo-PDF) ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="uh-beat-7">
        <Reveal>
          <Kicker icon={FileText}>{t.uh.b7Kicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground max-w-2xl">{t.uh.b7Title}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-2xl">{t.uh.b7Text}</p>
          <div className="mt-5 flex flex-wrap gap-2" data-testid="uh-pdf-contents">
            {t.uh.b7Chips.map((c) => (
              <Chip key={c}>{c}</Chip>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.1} className="mt-9">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5">
            {UH_BEAT_ASSETS.pdfPages.map((p, i) => (
              <a
                key={p.src}
                data-testid={`uh-pdf-page-${i + 1}`}
                href={UH_BEAT_ASSETS.pdfFile}
                target="_blank"
                rel="noopener noreferrer"
                className="block overflow-hidden rounded-lg border border-white/15 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.35)] hover:border-[#22d3ee]/60 transition-[border-color,transform] duration-200 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Shot asset={p} />
              </a>
            ))}
          </div>
          <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-4">
            <a
              data-testid="uh-pdf-button"
              href={UH_BEAT_ASSETS.pdfFile}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 self-start rounded-full border px-5 h-10 text-xs font-mono-cs uppercase tracking-[0.12em] transition-[background-color,border-color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              style={{ borderColor: "rgba(34,211,238,0.4)", color: CYAN, backgroundColor: "rgba(34,211,238,0.06)" }}
            >
              <FileText className="w-3.5 h-3.5" />
              {t.uh.b7PdfButton}
            </a>
            <p className="text-xs font-mono-cs text-muted-foreground/60 leading-relaxed" data-testid="uh-pdf-note">
              {t.uh.b7PdfNote}
            </p>
          </div>
        </Reveal>
      </div>

      {/* ---- Beat 8 — Betreiber-Dashboard & Leadflow ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="uh-beat-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-5">
            <Kicker icon={LayoutDashboard}>{t.uh.b8Kicker}</Kicker>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.uh.b8Title}</h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">{t.uh.b8Text}</p>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7">
            <div className="flex justify-center gap-6 sm:gap-10">
              <div className="w-[200px] sm:w-[240px] mt-10">
                <DeviceFramePhone testId="uh-briefing-phone">
                  <Shot asset={UH_BEAT_ASSETS.briefingMobile} />
                </DeviceFramePhone>
              </div>
              <div className="w-[200px] sm:w-[240px]">
                <DeviceFramePhone testId="uh-kontakt-phone">
                  <Shot asset={UH_BEAT_ASSETS.kontaktMobile} />
                </DeviceFramePhone>
              </div>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="mt-12">
          <DeviceFrameDesktop url="unfallheld.com/admin" testId="uh-dashboard-frame">
            <Shot asset={UH_BEAT_ASSETS.dashboardDesktop} />
          </DeviceFrameDesktop>
        </Reveal>
        <Reveal delay={0.1} className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <DeviceFrameDesktop url="unfallheld.com/admin" testId="uh-falldetail-frame">
              <Shot asset={UH_BEAT_ASSETS.falldetailDesktop} />
            </DeviceFrameDesktop>
          </div>
          <div className="lg:col-span-4 flex justify-center">
            <div className="w-[230px] sm:w-[260px]">
              <DeviceFramePhone testId="uh-dashboard-phone">
                <Shot asset={UH_BEAT_ASSETS.dashboardMobile} />
              </DeviceFramePhone>
            </div>
          </div>
        </Reveal>
      </div>

      {/* ---- Live Flow ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="uh-beat-flow">
        <Reveal>
          <Kicker>{t.uh.flowKicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.uh.flowTitle}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-xl">{t.uh.flowText}</p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal delay={0.08} className="lg:col-span-8">
            <DeviceFrameDesktop url="unfallheld.com" testId="uh-flow-desktop-frame">
              <LazyVideo
                src={UH_BEAT_ASSETS.flowDesktop.src}
                webm={UH_BEAT_ASSETS.flowDesktop.webm}
                poster={UH_BEAT_ASSETS.flowDesktop.poster}
                width={UH_BEAT_ASSETS.flowDesktop.w}
                height={UH_BEAT_ASSETS.flowDesktop.h}
                testId="uh-flow-desktop-video"
              />
            </DeviceFrameDesktop>
          </Reveal>
          <Reveal delay={0.16} className="lg:col-span-4 flex justify-center">
            <div className="w-[230px] sm:w-[260px]">
              <DeviceFramePhone testId="uh-flow-phone-frame">
                <LazyVideo
                  src={UH_BEAT_ASSETS.flowMobile.src}
                  webm={UH_BEAT_ASSETS.flowMobile.webm}
                  poster={UH_BEAT_ASSETS.flowMobile.poster}
                  width={UH_BEAT_ASSETS.flowMobile.w}
                  height={UH_BEAT_ASSETS.flowMobile.h}
                  testId="uh-flow-mobile-video"
                />
              </DeviceFramePhone>
            </div>
          </Reveal>
        </div>
      </div>

      {/* ---- Rechtlicher Hinweis ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-20">
        <Reveal>
          <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid="uh-legal-note">
            <Info className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground/70" />
            <p className="text-xs font-mono-cs text-muted-foreground/70 leading-relaxed">{t.uh.legalNote}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
